import type { z } from 'zod/v4';
import { captureMessage } from '@sentry/angular';

export type ParseResponseMeta = {
    /** Stable endpoint identifier (templated, no interpolated ids) used for Sentry fingerprinting. */
    endpoint?: string;
    method?: string;
};

/**
 * Strict mode flips contract mismatches from "report and pass through" to "throw".
 * Off by default (the rollout default) so a wrong schema can never break prod; apps
 * opt in via `provideIntegrations({ strict })`.
 */
let contractsStrict = false;

export function setContractsStrict(strict: boolean): void {
    contractsStrict = strict;
}

/**
 * Validates an API response against its Zod schema.
 *
 * In strict mode a mismatch throws, giving a real runtime guarantee. Otherwise
 * (the rollout default) the raw response is returned unchanged, so a wrong schema
 * can never break prod — it only surfaces as a Sentry warning. Note that on the
 * non-strict failure path the raw response flows through without the schema's
 * transforms/preprocessors applied.
 *
 * Either way the mismatch is reported first: a strict-mode throw is swallowed into
 * TanStack Query's error state, so without this the failing field is invisible and
 * the UI shows a bare "unknown error" with nothing to debug from.
 */
export function parseResponse<S extends z.ZodType>(data: unknown, schema: S, meta?: ParseResponseMeta): z.infer<S> {
    const result = schema.safeParse(data);

    if (result.success) {
        return result.data;
    }

    console.warn('Contract validation failed', {
        endpoint: meta?.endpoint ?? 'unknown',
        method: meta?.method ?? 'unknown',
        issues: result.error.issues,
    });

    captureMessage('Contract validation failed', {
        level: 'warning',
        fingerprint: ['contract-validation', meta?.endpoint ?? 'unknown'],
        tags: {
            endpoint: meta?.endpoint ?? '',
            method: meta?.method ?? '',
        },
        extra: {
            issues: result.error.issues,
        },
    });

    if (contractsStrict) {
        throw result.error;
    }

    return data as z.infer<S>;
}
